import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ADMIN › IMPORT › KIR-IMPORT VARÁZSLÓ (backend/kir-import.js)
 *
 *  A KIR-ből letöltött exportok egy helyen, három lépésben:
 *    1. fájl(ok) feltöltése – a szerver felismeri, melyik export (tanulói / alkalmazotti
 *       nyilvántartás, gondviselők), és ha nem, megmondja, mi hiányzik;
 *    2. előnézet – új / módosuló / változatlan / hibás sorok, a módosulónál régi → új;
 *       ekkor még semmi nem íródik;
 *    3. jóváhagyás után a meglévő importerek írnak, a végén összesítő.
 *  A régi, egyfájlos import-képernyők (Tanuló, Gondviselő, Állomány) változatlanul megmaradnak –
 *  az osztályonkénti kézi hozzárendelés, az Entra-összefésülés és a Keycloak-fájl ott érhető el.
 */
@Component({
  selector: 'app-admin-kir-import-varazslo',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './kir-import-varazslo.component.html',
  styleUrls: ['./kir-import-varazslo.component.scss'],
})
export class AdminKirImportVarazsloComponent implements OnInit {
  intezmeny = '';
  lepes = 1;
  fajlok: File[] = [];
  ev: number | null = null;
  eltolas = 0;

  betolt = false;
  ir = false;
  hiba = '';

  elonezet: any = null;
  /** Fájlonként: írjuk-e (felismert fájlnál alapból igen). */
  kijelolt: Record<number, boolean> = {};
  /** Fájlonként: a módosuló sorokat is írja-e (alapból igen). */
  modosulokIs: Record<number, boolean> = {};
  /** Fájlonként a megjelenített állapot-szűrő. */
  szuro: Record<number, string> = {};
  /** Fájlonként hány sort mutatunk (nagy fájlnál lapozás helyett „továbbiak”). */
  mutat: Record<number, number> = {};
  eredmeny: any = null;

  readonly ALLAPOTOK: { kod: string; nev: string; szin: string }[] = [
    { kod: 'uj', nev: 'Új', szin: 'success' },
    { kod: 'modosulo', nev: 'Módosuló', szin: 'warning' },
    { kod: 'valtozatlan', nev: 'Változatlan', szin: 'medium' },
    { kod: 'hibas', nev: 'Hibás', szin: 'danger' },
  ];

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    this.intezmeny = String(sajat?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    if (!this.intezmeny) this.hiba = 'Nincs kiválasztott intézmény.';
    // A tanulók osztályhoz rendelése a MUNKATANÉV osztályai közül megy (mint a Tanuló importnál).
    try { this.ev = await this.data.getMunkatanev(); } catch (e) { this.ev = null; }
  }

  fajlValaszt(event: any) {
    const lista: any = event?.target?.files || [];
    const uj: File[] = [];
    for (let i = 0; i < lista.length; i++) uj.push(lista[i]);
    // Ugyanaz a fájl kétszer ne kerüljön be (név + méret).
    for (const f of uj) {
      if (!this.fajlok.some((x) => x.name === f.name && x.size === f.size)) this.fajlok.push(f);
    }
    if (event?.target) event.target.value = null;
  }

  fajlTorol(i: number) {
    this.fajlok.splice(i, 1);
  }

  meret(b: number): string {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  }

  /** 1 → 2. lépés: feltöltés és előnézet. A szerver ilyenkor még nem ír. */
  async elonezetKer() {
    if (!this.fajlok.length) return;
    this.betolt = true; this.hiba = ''; this.eredmeny = null;
    try {
      const fd = new FormData();
      for (const f of this.fajlok) fd.append('fajlok', f, f.name);
      if (this.ev) fd.append('ev', String(this.ev));
      fd.append('eltolas', String(Number(this.eltolas) || 0));
      this.elonezet = await firstValueFrom(this.http.post(`/api/kir-import/elonezet/${this.intezmeny}`, fd));
      this.kijelolt = {}; this.modosulokIs = {}; this.szuro = {}; this.mutat = {};
      for (const f of this.fajlokElonezet) {
        this.kijelolt[f.index] = !!f.felismerve && (f.osszesito?.uj || f.osszesito?.modosulo) > 0;
        this.modosulokIs[f.index] = true;
        // Elsőre azt mutatjuk, amivel dolga van: módosuló, aztán hibás, aztán új.
        this.szuro[f.index] = f.osszesito?.modosulo ? 'modosulo' : (f.osszesito?.hibas ? 'hibas' : (f.osszesito?.uj ? 'uj' : ''));
        this.mutat[f.index] = 100;
      }
      this.lepes = 2;
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült feldolgozni a fájlokat.';
    } finally { this.betolt = false; }
  }

  get fajlokElonezet(): any[] {
    return this.elonezet?.fajlok || [];
  }

  sorokSzurve(f: any): any[] {
    const sz = this.szuro[f.index];
    const lista: any[] = f.sorok || [];
    return sz ? lista.filter((s: any) => s.allapot === sz) : lista;
  }

  sorokMutat(f: any): any[] {
    return this.sorokSzurve(f).slice(0, this.mutat[f.index] || 100);
  }

  tovabbiak(f: any) {
    this.mutat[f.index] = (this.mutat[f.index] || 100) + 200;
  }

  db(f: any, kod: string): number {
    return Number(f?.osszesito?.[kod] || 0);
  }

  allapotNev(kod: string): string {
    return this.ALLAPOTOK.find((a) => a.kod === kod)?.nev || kod;
  }
  allapotSzin(kod: string): string {
    return this.ALLAPOTOK.find((a) => a.kod === kod)?.szin || 'medium';
  }

  /** Mennyi íródna a jelenlegi kijelöléssel – a jóváhagyó gomb ezt mondja ki. */
  get irandoDb(): { uj: number; modosulo: number; fajl: number } {
    let uj = 0; let modosulo = 0; let fajl = 0;
    for (const f of this.fajlokElonezet) {
      if (!f.felismerve || !this.kijelolt[f.index]) continue;
      fajl++;
      uj += f.osszesito?.uj || 0;
      if (this.modosulokIs[f.index]) modosulo += f.osszesito?.modosulo || 0;
    }
    return { uj, modosulo, fajl };
  }

  vissza() {
    this.lepes = 1;
    this.elonezet = null;
    this.eredmeny = null;
  }

  /** 2 → 3. lépés: jóváhagyott írás. */
  async iras() {
    const d = this.irandoDb;
    if (!d.fajl || (!d.uj && !d.modosulo)) return;
    if (!confirm(`${d.uj} új és ${d.modosulo} módosuló sor íródik be ${d.fajl} fájlból. Folytatod?`)) return;
    this.ir = true; this.hiba = '';
    try {
      const fajlok = this.fajlokElonezet.filter((f: any) => f.felismerve && this.kijelolt[f.index]).map((f: any) => f.index);
      this.eredmeny = await firstValueFrom(this.http.post(`/api/kir-import/iras/${this.intezmeny}`, {
        azonosito: this.elonezet.azonosito, fajlok, modosulokIs: this.modosulokIs,
      }));
      this.lepes = 3;
      const hibas = (this.eredmeny?.eredmeny || []).some((e: any) => e.hibak?.length);
      this.data.sendToast(hibas ? 'Az import részben hibára futott – lásd az összesítőt.' : 'Az import elkészült.', hibas ? 'warning' : 'success');
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az írás nem sikerült.';
    } finally { this.ir = false; }
  }

  ujra() {
    this.lepes = 1;
    this.fajlok = [];
    this.elonezet = null;
    this.eredmeny = null;
    this.hiba = '';
  }

  trackIndex = (_i: number, f: any) => f.index;
  trackSor = (_i: number, s: any) => `${s.sor}|${s.kulcs}`;
}
